// Audit — first-class per-project compliance surface, presented with the SAME
// Drive-style idiom as Apps/Agents/Workflows: a collapsible left rail listing
// the project's audit runs (plus the inherited platform layer), a detail pane
// on the right, and the primary action ("Run audit") in the rail footer.
//
// The audit domain lives on the soc2_auditor fork (one store install-wide);
// this page reads it through the thin /api/audit proxy, scoped to the CURRENT
// project (soc2_project.name == project slug). Checks execute on the agent
// fleet (queue kind audit_project); "Run audit" enqueues a run. The
// 'platform' project carries the infra layer (Prowler) inherited by every
// project. Audit numbering is per-project: the store assigns `seq` at
// record_audit — the global row id is never shown.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useProject } from "../contexts/ProjectContext";
import { fetchData } from "../fetchapi/fetchapi";
import { buildEvidencePackage, downloadBytes } from "../utils/evidenceDocx";
import { EVIDENCE_KINDS, kindForControl, isOverdue } from "../utils/evidenceTemplates";
import {
  ShieldCheck, Play, Loader2, RefreshCw, ChevronDown, ChevronRight,
  CircleCheck, CircleX, CircleAlert, Server, Wrench, PanelLeftClose,
  PanelLeftOpen, Gauge, ClipboardList, FileCheck2, Download, Plus, X, Trash2,
  Satellite, Zap,
} from "lucide-react";

const eng = (action, params) =>
  fetchData("audit/engine", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action, params }) });

const SEV_COLOR = { critical: "#dc2626", high: "#ea580c", medium: "#d97706", low: "#65a30d" };
const jsonish = (v, fb) => { if (v == null) return fb; if (typeof v === "string") { try { return JSON.parse(v); } catch { return fb; } } return v; };
const fmt = (v) => { try { return new Date(v).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return String(v || ""); } };

function StatusPill({ s, title }) {
  // A finding row leads with its GOVERNED state, not the raw scanner verdict:
  // a detection that has been accepted, resolved, or ruled a false positive
  // must not read as an outstanding failure. Raw "fail" survives only for
  // detections with no ledger disposition (i.e. genuinely open/new).
  const map = {
    pass: { bg: "var(--pill-green-strong-bg,#dcfce7)", color: "var(--pill-green-strong-color,#166534)", Icon: CircleCheck, label: "pass" },
    fail: { bg: "#fee2e2", color: "#b91c1c", Icon: CircleX, label: "fail" },
    open: { bg: "#fee2e2", color: "#b91c1c", Icon: CircleX, label: "open" },
    in_progress: { bg: "var(--pill-amber-bg,#fef3c7)", color: "var(--pill-amber-color,#92400e)", Icon: Wrench, label: "in progress" },
    accepted_risk: { bg: "#e0e7ff", color: "#3730a3", Icon: ShieldCheck, label: "accepted risk" },
    false_positive: { bg: "#e5e7eb", color: "#374151", Icon: CircleAlert, label: "false positive" },
    resolved: { bg: "var(--pill-green-strong-bg,#dcfce7)", color: "var(--pill-green-strong-color,#166534)", Icon: CircleCheck, label: "resolved" },
    manual: { bg: "var(--pill-amber-bg,#fef3c7)", color: "var(--pill-amber-color,#92400e)", Icon: CircleAlert, label: "verify manually" },
    // A control the platform owns and a platform audit tested — carried here
    // with that audit's verdict rather than re-tested per application.
    inherited: { bg: "#e0e7ff", color: "#3730a3", Icon: ShieldCheck, label: "inherited" },
    attested: { bg: "var(--pill-green-strong-bg,#dcfce7)", color: "var(--pill-green-strong-color,#166534)", Icon: FileCheck2, label: "attested" },
    needs_human: { bg: "var(--pill-amber-bg,#fef3c7)", color: "var(--pill-amber-color,#92400e)", Icon: CircleAlert, label: "needs attestation" },
  };
  const m = map[s] || map.manual;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap" title={title} style={{ background: m.bg, color: m.color }}>
      <m.Icon size={11} />{m.label}
    </span>
  );
}

// Governed state per check id: the ledger's "worst" status wins so an open
// issue is never masked by an older resolved row for the same check.
const DISPO_RANK = ["open", "in_progress", "accepted_risk", "false_positive", "resolved"];
function dispositionByCheck(issues) {
  const out = {};
  for (const i of issues || []) {
    if (!i.check_id) continue;
    const cur = out[i.check_id];
    if (!cur || DISPO_RANK.indexOf(i.status) < DISPO_RANK.indexOf(cur)) out[i.check_id] = i.status;
  }
  return out;
}

// Layer + detector tags: every issue carries WHERE it lives (code = the
// application/repo program, infra = the platform layer) and WHICH scanner or
// collector detected it — parsed from the first-seen audit's triggered_by
// (e.g. "auditor (prowler-aws, round 99)"), which issue_list joins in.
const LAYER_TAG = {
  code: { bg: "#dbeafe", color: "#1d4ed8", label: "code" },
  infra: { bg: "#f3e8ff", color: "#7c3aed", label: "infra" },
};
function detectorOf(i) {
  const t = String(i.detected_by || i.last_seen_by || "");
  if (t.includes("prowler-aws")) return "AWS scanner (Prowler)";
  if (t.includes("prowler")) return "K8s scanner (Prowler)";
  return "Code & config scanner";
}

// One issue: the whole row is clickable and expands to the full record —
// detection detail (which collector/scanner, which run), affected resources,
// the event timeline, and the resolution/disposition note.
function IssueRow({ i, onFix, busy }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);
  const resolved = !["open", "in_progress"].includes(i.status);
  const layer = LAYER_TAG[i._layer];
  const toggle = async () => {
    setOpen((v) => !v);
    if (!events) {
      try { const r = await eng("issue_get", { id: i.id, project_id: i.project_id }); setEvents(r.events || []); }
      catch { setEvents([]); }
    }
  };
  const resources = (() => {
    if (!i.evidence) return null;
    const j = jsonish(i.evidence, null);
    const s = Array.isArray(j) ? j.join(", ") : typeof j === "string" ? j : String(i.evidence);
    return s && s !== "[]" ? s : null;
  })();
  return (
    <div className="border border-page-divider rounded-md px-3 py-2" style={resolved ? { opacity: 0.75 } : undefined} data-testid={`issue-${i.id}`}>
      <div className="flex items-start gap-2 text-sm cursor-pointer" onClick={toggle} data-testid={`issue-toggle-${i.id}`}>
        <span className="mt-0.5 text-page-muted shrink-0">{open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</span>
        <span className="text-[10px] font-bold uppercase mt-1 shrink-0" style={{ color: SEV_COLOR[i.severity] || "#6b7280" }}>{i.severity}</span>
        <div className="flex-1 min-w-0">
          <div className={resolved ? "line-through decoration-1" : ""}>{i.title}</div>
          <div className="text-xs text-page-muted flex items-center gap-1.5 flex-wrap mt-0.5">
            {layer ? (
              <span data-testid={`issue-tag-${i._layer}`} className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ background: layer.bg, color: layer.color }}>{layer.label}</span>
            ) : null}
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ background: "#e5e7eb", color: "#374151" }}
              title={i.detected_by ? `detected by ${i.detected_by}` : "detected by the code & config scanner"}>
              {detectorOf(i)}
            </span>
            <span className="font-mono text-[10px]">{i.check_id || i.control_id}</span>
            <b style={{ color: resolved ? "#16a34a" : "#d97706" }}>{i.status}</b>
            <span>first seen {fmt(i.first_seen_at)}{i.detected_in_seq ? ` (run #${i.detected_in_seq})` : ""}</span>
            {resolved && i.resolved_at ? <span>· resolved {fmt(i.resolved_at)}</span> : null}
            {i.assigned_to ? <span>· {i.assigned_to}</span> : null}
          </div>
        </div>
        {!resolved && onFix ? (
          <button onClick={(e) => { e.stopPropagation(); onFix(i); }} disabled={busy} data-testid={`audit-fix-${i.id}`}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-page-bg disabled:opacity-50 shrink-0">
            <Wrench size={12} /> Fix with assistant
          </button>
        ) : null}
      </div>
      {open ? (
        <div className="mt-2 ml-6 space-y-2" data-testid={`issue-detail-${i.id}`}>
          {i.detail ? <div className="text-xs">{i.detail}</div> : null}
          {resources ? (
            <div className="text-xs text-page-muted break-words">
              <span className="font-medium">Affected resources:</span> <span className="font-mono text-[10px]">{resources}</span>
            </div>
          ) : null}
          <div className="text-xs text-page-muted">
            Detected by <b>{i.detected_by || "code & config scanner"}</b>
            {i.detected_in_seq ? <> in run #{i.detected_in_seq}</> : null}
            {" · last seen "}{fmt(i.last_seen_at)}{i.last_seen_seq ? ` (run #${i.last_seen_seq})` : ""}
          </div>
          <div className="space-y-1" data-testid="issue-timeline">
            {events === null ? <Loader2 size={13} className="animate-spin" /> :
              events.length === 0 ? <div className="text-xs text-page-muted">No recorded events.</div> :
              events.map((e) => (
                <div key={e.id} className="flex items-start gap-2 text-xs">
                  <span className="text-page-muted shrink-0 w-32">{fmt(e.at)}</span>
                  <span className="font-medium shrink-0" style={{ color: e.event === "resolved" || e.to_status === "resolved" ? "#16a34a" : e.event === "reopened" ? "#dc2626" : "var(--page-text,#111)" }}>{e.event}</span>
                  <span className="text-page-muted truncate">{e.actor}{e.note ? ` — ${e.note}` : ""}</span>
                </div>
              ))}
          </div>
          {i.resolution_note ? <div className="text-xs text-page-muted">resolution: {i.resolution_note}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

// Logs — the stored run history, one chronological stream: every collector
// run (soc2_collector_run, appended by collector_report) and every scanner
// run (code audits + infrastructure scans). Every row opens its run's
// detail page (RunDetailPane).
function LogsPane({ programId, platformId, programSlug, onOpen }) {
  const [entries, setEntries] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cr, al, pl, pi, pli] = await Promise.all([
          programId ? eng("collector_runs_list", { project_id: programId }) : Promise.resolve({ runs: [] }),
          programId ? eng("audit_list", { project_id: programId }) : Promise.resolve({ audits: [] }),
          platformId ? eng("audit_list", { project_id: platformId }) : Promise.resolve({ audits: [] }),
          programId ? eng("issue_list", { project_id: programId }).catch(() => ({ issues: [] })) : Promise.resolve({ issues: [] }),
          platformId ? eng("issue_list", { project_id: platformId }).catch(() => ({ issues: [] })) : Promise.resolve({ issues: [] }),
        ]);
        if (!alive) return;
        // Issues each scan run OPENED (first detected), shown on the row.
        const openedBy = {};
        for (const i of [...(pi.issues || []), ...(pli.issues || [])]) {
          if (i.first_seen_audit_id != null) openedBy[i.first_seen_audit_id] = (openedBy[i.first_seen_audit_id] || 0) + 1;
        }
        const rows = [];
        for (const r of cr.runs || []) rows.push({ id: `c${r.id}`, dbId: r.id, t: r.ran_at, kind: "collector", name: r.name || r.key, key: r.key, ok: r.ok !== false, result: r.result || "", projectId: programId });
        for (const a of al.audits || []) { const st = jsonish(a.stats, {}) || {}; rows.push({ id: `a${a.id}`, t: a.finished_at, kind: "scan", layer: "code", name: `Audit ${a.seq ?? a.id}`, by: a.triggered_by, stats: st, auditId: a.id, projectId: programId, opened: openedBy[a.id] || 0 }); }
        for (const a of pl.audits || []) { const st = jsonish(a.stats, {}) || {}; rows.push({ id: `p${a.id}`, t: a.finished_at, kind: "scan", layer: "infra", name: `Infrastructure scan #${a.seq ?? a.id}`, by: a.triggered_by, stats: st, auditId: a.id, projectId: platformId, opened: openedBy[a.id] || 0 }); }
        rows.sort((x, y) => new Date(y.t) - new Date(x.t));
        setEntries(rows);
      } catch { if (alive) setEntries([]); }
    })();
  return () => { alive = false; };
  }, [programId, platformId]);
  if (!programId) return <div className="p-6 text-sm text-page-muted">This program is not tracked in the audit store yet.</div>;
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="audit-logs-pane">
      <div>
        <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2">
          <ClipboardList size={20} /> Run logs
        </h1>
        <div className="text-sm text-page-muted mt-1">
          Stored history of every collector and scanner run for <b>{programSlug}</b> — newest first.
          Scanner rows expand to the findings recorded by that run.
        </div>
      </div>
      {entries === null ? <Loader2 size={16} className="animate-spin" /> :
        entries.length === 0 ? <div className="text-sm text-page-muted" data-testid="audit-logs-empty">No runs logged yet — collector history starts recording from their next run.</div> : (
          <div className="space-y-1" data-testid="audit-logs-list">
            {entries.map((e) => (
              <div
                key={e.id}
                data-testid={`log-${e.id}`}
                onClick={() => onOpen?.(e)}
                className="border border-page-divider rounded-md px-3 py-1.5 text-xs cursor-pointer hover:bg-card-border/20"
              >
                <div className="flex items-start gap-2">
                  <span className="text-page-muted shrink-0 w-32">{fmt(e.t)}</span>
                  {e.kind === "scan" ? (
                    <span data-testid={`log-tag-${e.layer}`} className="text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0" style={{ background: LAYER_TAG[e.layer].bg, color: LAYER_TAG[e.layer].color }}>{LAYER_TAG[e.layer].label}</span>
                  ) : (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0" style={{ background: "#e5e7eb", color: "#374151" }}>collector</span>
                  )}
                  {e.kind === "collector"
                    ? (e.ok
                        ? <CircleCheck size={13} className="shrink-0 mt-0.5" style={{ color: "#16a34a" }} />
                        : <CircleX size={13} className="shrink-0 mt-0.5" style={{ color: "#dc2626" }} />)
                    : ((e.stats.fail || 0) > 0
                        ? <CircleX size={13} className="shrink-0 mt-0.5" style={{ color: "#dc2626" }} />
                        : <CircleCheck size={13} className="shrink-0 mt-0.5" style={{ color: "#16a34a" }} />)}
                  <span className="font-medium shrink-0">{e.name}</span>
                  <span className="text-page-muted min-w-0 flex-1">
                    {e.kind === "collector"
                      ? e.result
                      : <>by {e.by || "—"} · {e.stats.pass != null
                          ? `${e.stats.pass} pass / ${e.stats.fail || 0} fail${e.stats.manual ? ` / ${e.stats.manual} manual` : ""}`
                          : `${e.stats.fail || 0} detections${e.stats.manual ? ` · ${e.stats.manual} manual` : ""}`}
                          {e.opened ? <b> · {e.opened} issue(s) opened</b> : null}</>}
                  </span>
                  <span className="text-page-muted shrink-0"><ChevronRight size={12} /></span>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// Elapsed time between two timestamps, humanized; null when either is missing.
function dur(a, b) {
  try {
    const ms = new Date(b) - new Date(a);
    if (!(ms >= 0)) return null;
    const s = Math.round(ms / 1000);
    return s < 90 ? `${s}s` : `${Math.round(s / 60)}m`;
  } catch { return null; }
}

// Detail page for ONE logged run, opened from a Logs row. Scanner runs show
// start/finish, stats, the issues this run OPENED, and every recorded
// finding; collector runs show start/finish, the result, the collector's
// type-specific run details (meta), and the evidence record they produced.
// Findings rows shared by the scan page and the heartbeat page — each row
// leads with its governed state, the raw verdict only when open/new.
function FindingRows({ findings, dispo }) {
  const shownStatus = (f) => (f.status !== "fail" ? f.status : (dispo || {})[f.check_id] || "fail");
  return (findings || []).length === 0 ? <div className="text-xs text-page-muted">No findings recorded.</div> : (
    <div className="space-y-0.5">
      {findings.map((f, i) => (
        <div key={i} className={"flex items-start gap-2 text-xs py-1.5 " + (i ? "border-t border-page-divider" : "")}>
          <StatusPill s={shownStatus(f)} title={shownStatus(f) !== f.status ? `scanner verdict: ${f.status} (detected) · governed state: ${shownStatus(f)}` : undefined} />
          <span className="font-mono text-[10px] text-page-muted mt-0.5">{f.control_id || f.check_id}</span>
          <span className="flex-1">{f.title}{f.detail ? <span className="text-page-muted"> — {f.detail}</span> : null}</span>
          {f.severity ? <span className="text-[10px] font-bold uppercase" style={{ color: SEV_COLOR[f.severity] || "#6b7280" }}>{f.severity}</span> : null}
        </div>
      ))}
    </div>
  );
}

function RunDetailPane({ runRef, platformId, onBack, onJumpControl, onOpenRun }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    (async () => {
      try {
        if (runRef.type === "scan") {
          const [r, il] = await Promise.all([
            eng("audit_get", { id: runRef.id, project_id: runRef.projectId }),
            eng("issue_list", { project_id: runRef.projectId }).catch(() => ({ issues: [] })),
          ]);
          if (!alive) return;
          const issues = il.issues || [];
          setData({
            audit: r.audit || {},
            findings: r.findings || [],
            dispo: dispositionByCheck(issues),
            opened: issues.filter((i) => String(i.first_seen_audit_id) === String(runRef.id)),
          });
        } else {
          const r = await eng("collector_run_get", { id: runRef.id, project_id: runRef.projectId });
          const run = r.run || {};
          const meta = jsonish(run.meta, {}) || {};
          let evidence = null;
          if (meta.evidence_id) {
            try { evidence = (await eng("evidence_get", { id: meta.evidence_id, project_id: runRef.projectId })).evidence || null; }
            catch { evidence = null; }
          }
          // Scanner heartbeats (prowler_*, repo_scanner) don't carry findings
          // themselves — they point at the SCAN run that does. Resolve it so
          // the page can link straight to the findings.
          let scan = null;
          if (meta.audit_seq != null) {
            const scanLayer = meta.layer === "infra" ? "infra" : "code";
            const scanProject = scanLayer === "infra" ? platformId : runRef.projectId;
            if (scanProject) {
              try {
                const al = await eng("audit_list", { project_id: scanProject });
                const a = (al.audits || []).find((x) => String(x.seq) === String(meta.audit_seq));
                if (a) {
                  // Pull the scan's findings + opened issues so the heartbeat
                  // page shows them INLINE — not just a pointer.
                  const [ag, il2] = await Promise.all([
                    eng("audit_get", { id: a.id, project_id: scanProject }),
                    eng("issue_list", { project_id: scanProject }).catch(() => ({ issues: [] })),
                  ]);
                  const scanIssues = il2.issues || [];
                  scan = {
                    runId: (scanLayer === "infra" ? "p" : "a") + a.id, layer: scanLayer, seq: a.seq,
                    stats: jsonish(a.stats, {}) || {},
                    findings: ag.findings || [],
                    dispo: dispositionByCheck(scanIssues),
                    opened: scanIssues.filter((i) => String(i.first_seen_audit_id) === String(a.id)),
                  };
                }
              } catch { scan = null; }
            }
          }
          if (!alive) return;
          setData({ run, meta, evidence, scan });
        }
      } catch { if (alive) setData({ error: true }); }
    })();
    return () => { alive = false; };
    // platformId arrives async on a cold deep link — the heartbeat's scan
    // resolution needs it, so the fetch must re-run when it lands.
  }, [runRef.type, runRef.id, runRef.projectId, platformId]);

  const Back = (
    <button onClick={onBack} data-testid="run-detail-back"
      className="inline-flex items-center gap-1 text-xs bg-transparent border-0 cursor-pointer text-page-muted hover:text-page-text p-0">
      <ChevronRight size={12} style={{ transform: "rotate(180deg)" }} /> Run logs
    </button>
  );

  if (data === null) {
    return <div className="p-6 max-w-4xl mx-auto space-y-3" data-testid="run-detail-pane">{Back}<Loader2 size={16} className="animate-spin" /></div>;
  }
  if (data.error) {
    return <div className="p-6 max-w-4xl mx-auto space-y-3" data-testid="run-detail-pane">{Back}<div className="text-sm text-red-600">Failed to load this run.</div></div>;
  }

  if (runRef.type === "scan") {
    const a = data.audit || {};
    const st = jsonish(a.stats, {}) || {};
    const opened = data.opened || [];
    const d = dur(a.started_at, a.finished_at);
    const label = runRef.layer === "infra" ? `Infrastructure scan #${a.seq ?? a.id ?? ""}` : `Audit ${a.seq ?? a.id ?? ""}`;
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="run-detail-pane">
        {Back}
        <div>
          <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2">
            <ShieldCheck size={20} /> {label}
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ background: LAYER_TAG[runRef.layer].bg, color: LAYER_TAG[runRef.layer].color }}>{LAYER_TAG[runRef.layer].label}</span>
          </h1>
          <div className="text-sm text-page-muted mt-1" data-testid="run-detail-times">
            by {a.triggered_by || "—"}
            {a.started_at ? <> · started {fmt(a.started_at)}</> : null}
            {" · finished "}{fmt(a.finished_at)}{d ? ` (${d})` : ""}
            {a.git_branch ? <> · {a.git_branch}{a.git_commit ? `@${String(a.git_commit).slice(0, 8)}` : ""}</> : null}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {st.pass != null ? (
            <>
              <div className="rounded-lg border border-page-divider p-3"><div className="text-xs text-page-muted">Pass</div><div className="text-2xl font-semibold" style={{ color: "#16a34a" }}>{st.pass}</div></div>
              <div className="rounded-lg border border-page-divider p-3"><div className="text-xs text-page-muted">Fail</div><div className="text-2xl font-semibold" style={{ color: (st.fail || 0) > 0 ? "#dc2626" : "inherit" }}>{st.fail || 0}</div></div>
            </>
          ) : (
            <div className="rounded-lg border border-page-divider p-3"><div className="text-xs text-page-muted">Detections</div><div className="text-2xl font-semibold">{st.fail || 0}</div></div>
          )}
          <div className="rounded-lg border border-page-divider p-3"><div className="text-xs text-page-muted">To verify manually</div><div className="text-2xl font-semibold">{st.manual || 0}</div></div>
          <div className="rounded-lg border border-page-divider p-3"><div className="text-xs text-page-muted">Issues opened by this run</div><div className="text-2xl font-semibold">{opened.length}</div></div>
        </div>
        {opened.length ? (
          <div data-testid="run-detail-opened">
            <h2 className="text-sm font-semibold text-page-title mb-2">Issues opened by this run</h2>
            <div className="space-y-1.5">
              {opened.map((i) => <IssueRow key={i.id} i={{ ...i, _layer: runRef.layer }} onFix={null} busy={false} />)}
            </div>
          </div>
        ) : null}
        <div>
          <h2 className="text-sm font-semibold text-page-title mb-2">All findings recorded by this run</h2>
          <div className="border border-page-divider rounded-md px-3 py-2">
            <FindingRows findings={data.findings} dispo={data.dispo} />
          </div>
        </div>
      </div>
    );
  }

  // collector run detail
  const r = data.run || {};
  const meta = data.meta || {};
  const detailRows = Object.entries(meta).filter(([k]) => !["run_started_at", "evidence_id"].includes(k));
  const d = meta.run_started_at ? dur(meta.run_started_at, r.ran_at) : null;
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="run-detail-pane">
      {Back}
      <div>
        <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2">
          <Satellite size={20} /> {r.name || r.key}
          {r.ok !== false
            ? <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ background: "var(--pill-green-strong-bg,#dcfce7)", color: "var(--pill-green-strong-color,#166534)" }}>ok</span>
            : <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ background: "#fee2e2", color: "#b91c1c" }}>error</span>}
        </h1>
        <div className="text-sm text-page-muted mt-1" data-testid="run-detail-times">
          {r.source || "collector"}
          {meta.run_started_at ? <> · started {fmt(meta.run_started_at)}</> : null}
          {" · finished "}{fmt(r.ran_at)}{d ? ` (${d})` : ""}
        </div>
      </div>
      <div className="border border-page-divider rounded-md px-3 py-2 text-sm" data-testid="run-detail-result">
        <div className="text-xs text-page-muted mb-1">Result</div>
        {r.result || "—"}
      </div>
      {detailRows.length ? (
        <div data-testid="run-detail-meta">
          <h2 className="text-sm font-semibold text-page-title mb-2">Run details</h2>
          <dl className="space-y-1">
            {detailRows.map(([k, v]) => (
              <div key={k} className="flex gap-2 text-xs">
                <dt className="w-44 shrink-0 text-page-muted">{k.replace(/_/g, " ")}</dt>
                <dd className="min-w-0">{typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}
      {data.evidence ? (
        <div className="border border-page-divider rounded-md px-3 py-2" data-testid="run-detail-evidence">
          <div className="text-xs text-page-muted mb-1">Evidence record produced by this run</div>
          <div className="flex items-center gap-2 text-sm">
            <FileCheck2 size={14} style={{ color: data.evidence.status === "final" ? "#16a34a" : "var(--page-muted,#6b7280)" }} />
            <span className="min-w-0 flex-1 truncate">#{data.evidence.id} {data.evidence.title}</span>
            <SourcePill s={data.evidence.source} />
            <button onClick={() => onJumpControl?.(data.evidence.control_id)}
              className="text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer text-page-muted hover:text-page-text shrink-0">
              View in register
            </button>
          </div>
        </div>
      ) : null}
      {data.scan ? (
        // A scanner heartbeat points at the SCAN run that holds the findings —
        // show them right here, plus the link to the scan's own page.
        <div className="space-y-4" data-testid="run-detail-scan">
          <div className="border border-page-divider rounded-md px-3 py-2 flex items-center gap-2 text-sm">
            <ShieldCheck size={14} className="shrink-0" />
            <span className="min-w-0 flex-1">
              Findings from <b>{data.scan.layer === "infra" ? `Infrastructure scan #${data.scan.seq}` : `Audit ${data.scan.seq}`}</b>
              {" — "}{data.scan.stats.pass != null
                ? `${data.scan.stats.pass || 0} pass / ${data.scan.stats.fail || 0} fail`
                : `${data.scan.stats.fail || 0} detections`}
              {` · ${data.scan.opened.length} issue(s) opened`}
            </span>
            <button onClick={() => onOpenRun?.(data.scan.runId)} data-testid="run-detail-scan-open"
              className="text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer text-page-muted hover:text-page-text shrink-0">
              Open scan page
            </button>
          </div>
          {data.scan.opened.length ? (
            <div data-testid="run-detail-opened">
              <h2 className="text-sm font-semibold text-page-title mb-2">Issues opened by this scan</h2>
              <div className="space-y-1.5">
                {data.scan.opened.map((i) => <IssueRow key={i.id} i={{ ...i, _layer: data.scan.layer }} onFix={null} busy={false} />)}
              </div>
            </div>
          ) : null}
          <div>
            <h2 className="text-sm font-semibold text-page-title mb-2">All findings recorded by this scan</h2>
            <div className="border border-page-divider rounded-md px-3 py-2">
              <FindingRows findings={data.scan.findings} dispo={data.scan.dispo} />
            </div>
          </div>
        </div>
      ) : null}
      {r.description ? <div className="text-xs text-page-muted">{r.description}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Evidence layer — the SOC 2 Type II operating model: a control REGISTER
// (owner / frequency / required evidence per control) and EVIDENCE RECORDS,
// each a filled form for one control occurrence. Provenance is first-class:
// 'logged' (written when it happened), 'compiled' (assembled from named
// pre-existing system records), 'ceremony' (a review/test performed and
// recorded now). Sign-off turns a draft into an immutable 'final' record.
// ---------------------------------------------------------------------------
const SRC_BADGE = {
  logged: { bg: "#dbeafe", color: "#1d4ed8" },
  compiled: { bg: "#f3e8ff", color: "#7c3aed" },
  ceremony: { bg: "var(--pill-green-strong-bg,#dcfce7)", color: "var(--pill-green-strong-color,#166534)" },
  // Synthetic demo/test data — the engine refuses to sign these final, and
  // the UI offers no sign-off for them.
  sample: { bg: "var(--pill-amber-bg,#fef3c7)", color: "var(--pill-amber-color,#92400e)" },
};

function SourcePill({ s }) {
  const m = SRC_BADGE[s] || { bg: "#e5e7eb", color: "#374151" };
  return <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ background: m.bg, color: m.color }}>{s}</span>;
}

// Generic renderer for an evidence record's form JSON: a fields block plus
// optional table sections — the same shape every evidence kind uses, so a new
// kind needs no UI change.
function FormView({ form }) {
  const f = jsonish(form, {}) || {};
  return (
    <div className="space-y-3">
      {f.fields ? (
        <dl className="space-y-1">
          {Object.entries(f.fields).map(([k, v]) => (
            <div key={k} className="flex gap-2 text-xs">
              <dt className="w-40 shrink-0 text-page-muted">{k}</dt>
              <dd className="min-w-0">{String(v)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {(f.sections || []).map((s, i) => (
        <div key={i}>
          <div className="text-xs font-medium mb-1">{s.title}</div>
          <div className="overflow-x-auto">
            <table className="text-xs w-full border-collapse">
              <thead><tr>{(s.columns || []).map((c) => <th key={c} className="text-left pr-3 pb-1 text-page-muted font-medium whitespace-nowrap">{c}</th>)}</tr></thead>
              <tbody>
                {(s.rows || []).map((row, j) => (
                  <tr key={j} className="border-t border-page-divider">{(row || []).map((c, k) => <td key={k} className="pr-3 py-1 align-top">{String(c)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// One evidence record: header row expands to the full form, its provenance
// pointers, and — for drafts — the sign-off action (final records are
// immutable in the engine).
function EvidenceRow({ r, projectId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(null);
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setOpen((v) => !v);
    if (!full) {
      try { const res = await eng("evidence_get", { id: r.id, project_id: projectId }); setFull(res.evidence || null); }
      catch { setFull(null); }
    }
  };
  const signOff = async () => {
    if (!window.confirm("Sign off this evidence record? It becomes final and can never be edited.")) return;
    setBusy(true);
    try { await eng("evidence_patch", { id: r.id, project_id: projectId, status: "final" }); onChanged?.(); }
    finally { setBusy(false); }
  };
  const period = r.period_start ? `${String(r.period_start).slice(0, 10)} → ${String(r.period_end || "").slice(0, 10)}` : null;
  return (
    <div className="border border-page-divider rounded-md px-3 py-2" data-testid={`evidence-${r.id}`}>
      <div className="flex items-start gap-2 text-sm">
        <button onClick={toggle} className="bg-transparent border-0 cursor-pointer p-0 mt-0.5 text-page-muted" data-testid={`evidence-toggle-${r.id}`}>
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>
        <FileCheck2 size={14} className="mt-0.5 shrink-0" style={{ color: r.status === "final" ? "#16a34a" : "var(--page-muted,#6b7280)" }} />
        <div className="flex-1 min-w-0">
          <div className="truncate">{r.title}</div>
          <div className="text-xs text-page-muted">
            <span className="font-mono text-[10px]">{r.control_id}</span> · {r.kind}
            {period ? ` · covers ${period}` : ""} · recorded {fmt(r.created_at)} by {r.created_by || "—"}
            {r.status === "final" ? ` · signed by ${r.signed_by} ${fmt(r.signed_at)}` : ""}
          </div>
        </div>
        <SourcePill s={r.source} />
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={r.status === "final" ? { background: "var(--pill-green-strong-bg,#dcfce7)", color: "var(--pill-green-strong-color,#166534)" } : { background: "var(--pill-amber-bg,#fef3c7)", color: "var(--pill-amber-color,#92400e)" }}>{r.status}</span>
      </div>
      {open ? (
        <div className="mt-2 ml-6 space-y-3" data-testid={`evidence-detail-${r.id}`}>
          {full === null ? <Loader2 size={13} className="animate-spin" /> : (
            <>
              <FormView form={full.form} />
              {(jsonish(full.compiled_from, []) || []).length ? (
                <div className="text-xs text-page-muted">
                  <span className="font-medium">Compiled from:</span>{" "}
                  {(jsonish(full.compiled_from, []) || []).map((c, i) => (
                    <span key={i} className="font-mono text-[10px]">{c.system}{c.table ? `/${c.table}` : ""}{c.repo ? ` repo=${c.repo}` : ""}{c.population != null ? ` (population ${c.population})` : ""}{i < (jsonish(full.compiled_from, []) || []).length - 1 ? "; " : ""}</span>
                  ))}
                </div>
              ) : null}
              {r.status !== "final" && r.source !== "sample" ? (
                <button onClick={signOff} disabled={busy} data-testid={`evidence-sign-${r.id}`}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-page-bg disabled:opacity-50">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <CircleCheck size={12} />} Sign off (make final)
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

// Record-evidence modal: the manual intake path for evidence a human performs
// (a ceremony like an access review or DR test) or logs as it happens. The
// form is pre-shaped by the control's usual evidence kind; compiled evidence
// comes from the automated collectors (never hand-typed), and sample data
// only from the demo seeder — so the source choice here is ceremony | logged.
function RecordEvidenceModal({ control, projectId, onClose, onSaved }) {
  const kind0 = kindForControl(control.control_id);
  const [kind, setKind] = useState(kind0);
  const tpl = EVIDENCE_KINDS[kind] || EVIDENCE_KINDS.generic;
  const today = new Date().toISOString().slice(0, 10);
  const [title, setTitle] = useState(`${control.control} — ${today}`);
  const [source, setSource] = useState("ceremony");
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [fields, setFields] = useState(() => Object.fromEntries((EVIDENCE_KINDS[kind0] || EVIDENCE_KINDS.generic).fields.map((f) => [f.key, f.key === "Control Mapping" ? `${control.control_id} / ${control.tsc}` : ""])));
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const switchKind = (k) => {
    setKind(k);
    const t = EVIDENCE_KINDS[k] || EVIDENCE_KINDS.generic;
    setFields(Object.fromEntries(t.fields.map((f) => [f.key, f.key === "Control Mapping" ? `${control.control_id} / ${control.tsc}` : ""])));
    setRows([]);
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const form = { fields: Object.fromEntries(Object.entries(fields).filter(([, v]) => String(v).trim() !== "")) };
      if (tpl.section && rows.length) form.sections = [{ title: tpl.section.title, columns: tpl.section.columns, rows }];
      const r = await eng("evidence_record", {
        evidence: {
          project_id: projectId, control_id: control.control_id, kind, title,
          period_start: start || null, period_end: end || null,
          source, compiled_from: [], form,
        },
      });
      if (!r?.evidence?.id) throw new Error("save failed");
      onSaved?.();
      onClose();
    } catch (e) { setErr(String(e?.message || "Failed to save the record.")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(15,23,42,.45)" }} data-testid="record-evidence-modal">
      <div className="bg-page-bg rounded-lg shadow-xl w-[720px] max-h-[85vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center gap-2">
          <FileCheck2 size={18} className="text-nav-active" />
          <h2 className="text-base font-semibold text-page-title flex-1">Record evidence — {control.control_id} {control.control}</h2>
          <button onClick={onClose} className="bg-transparent border-0 cursor-pointer text-page-muted hover:text-page-text p-1" data-testid="record-evidence-close"><X size={16} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="col-span-2 space-y-1">
            <div className="text-xs text-page-muted">Title</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="re-title"
              className="w-full border border-page-divider rounded-md px-2 py-1.5 bg-transparent text-page-text" />
          </label>
          <label className="space-y-1">
            <div className="text-xs text-page-muted">Form (evidence kind)</div>
            <select value={kind} onChange={(e) => switchKind(e.target.value)} data-testid="re-kind"
              className="w-full border border-page-divider rounded-md px-2 py-1.5 bg-transparent text-page-text">
              {Object.entries(EVIDENCE_KINDS).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <div className="text-xs text-page-muted">Provenance</div>
            <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="re-source"
              className="w-full border border-page-divider rounded-md px-2 py-1.5 bg-transparent text-page-text">
              <option value="ceremony">ceremony — performed & recorded now</option>
              <option value="logged">logged — written as it happens</option>
            </select>
          </label>
          <label className="space-y-1">
            <div className="text-xs text-page-muted">Period start</div>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="re-start"
              className="w-full border border-page-divider rounded-md px-2 py-1.5 bg-transparent text-page-text" />
          </label>
          <label className="space-y-1">
            <div className="text-xs text-page-muted">Period end</div>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="w-full border border-page-divider rounded-md px-2 py-1.5 bg-transparent text-page-text" />
          </label>
        </div>

        <div className="space-y-2">
          {(tpl.fields || []).map((f) => (
            <label key={f.key} className="block space-y-1 text-sm">
              <div className="text-xs text-page-muted">{f.key}</div>
              <input value={fields[f.key] ?? ""} onChange={(e) => setFields((p) => ({ ...p, [f.key]: e.target.value }))}
                placeholder={f.hint} data-testid={`re-field-${f.key.replace(/\W+/g, "_")}`}
                className="w-full border border-page-divider rounded-md px-2 py-1.5 bg-transparent text-page-text placeholder:text-page-muted/60" />
            </label>
          ))}
        </div>

        {tpl.section ? (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="text-xs font-medium text-page-title">{tpl.section.title}</div>
              <button onClick={() => setRows((p) => [...p, tpl.section.columns.map(() => "")])} data-testid="re-add-row"
                className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border border-page-divider bg-transparent cursor-pointer text-page-muted hover:text-page-text">
                <Plus size={11} /> row
              </button>
            </div>
            {rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="text-xs w-full border-collapse">
                  <thead><tr>{tpl.section.columns.map((c) => <th key={c} className="text-left pr-2 pb-1 text-page-muted font-medium whitespace-nowrap">{c}</th>)}<th /></tr></thead>
                  <tbody>
                    {rows.map((row, ri) => (
                      <tr key={ri}>
                        {tpl.section.columns.map((_, ci) => (
                          <td key={ci} className="pr-2 py-0.5">
                            <input value={row[ci] ?? ""} onChange={(e) => setRows((p) => p.map((r, i) => i === ri ? r.map((c, j) => j === ci ? e.target.value : c) : r))}
                              data-testid={`re-cell-${ri}-${ci}`}
                              className="w-full border border-page-divider rounded px-1.5 py-1 bg-transparent text-page-text" />
                          </td>
                        ))}
                        <td><button onClick={() => setRows((p) => p.filter((_, i) => i !== ri))} className="bg-transparent border-0 cursor-pointer text-page-muted hover:text-red-600 p-1"><Trash2 size={12} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}

        {err ? <div className="text-xs text-red-600">{err}</div> : null}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer text-page-muted hover:text-page-text">Cancel</button>
          <button onClick={save} disabled={busy || !title.trim()} data-testid="re-save"
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border-0 cursor-pointer text-white disabled:opacity-50" style={{ background: "var(--nav-active,#2563eb)" }}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FileCheck2 size={14} />} Save draft
          </button>
        </div>
        <div className="text-[11px] text-page-muted">
          Saves as a draft you can review and sign off (sign-off makes it immutable). Compiled evidence is generated by the automated collectors and cannot be hand-typed.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collectors — the automated evidence sources (Vanta-style integrations).
// Each collector lists the register controls it evidences; config is
// non-secret settings plus the NAME of the credential the runner reads
// locally — secrets are never stored in the register or sent to a browser.
// ---------------------------------------------------------------------------
const COLLECTOR_STATUS = {
  active: { bg: "var(--pill-green-strong-bg,#dcfce7)", color: "var(--pill-green-strong-color,#166534)", label: "active" },
  needs_config: { bg: "var(--pill-amber-bg,#fef3c7)", color: "var(--pill-amber-color,#92400e)", label: "needs configuration" },
  disabled: { bg: "#e5e7eb", color: "#374151", label: "disabled" },
};

function CollectorCard({ c, projectId, onChanged, onControlClick }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(() => jsonish(c.config, {}) || {});
  const [runs, setRuns] = useState(null); // stored run history for THIS collector
  const controls = jsonish(c.controls, []) || [];
  const st = COLLECTOR_STATUS[c.status] || COLLECTOR_STATUS.needs_config;
  const patch = async (params) => {
    setBusy(true);
    try { await eng("collector_patch", { project_id: projectId, key: c.key, ...params }); onChanged?.(); }
    finally { setBusy(false); }
  };
  const toggle = async () => {
    setOpen((v) => !v);
    if (runs === null) {
      try { const r = await eng("collector_runs_list", { project_id: projectId, key: c.key, limit: 20 }); setRuns(r.runs || []); }
      catch { setRuns([]); }
    }
  };
  return (
    <div className="border border-page-divider rounded-md px-3 py-2" data-testid={`collector-${c.key}`}>
      <div className="flex items-start gap-2 text-sm">
        <button onClick={toggle} className="bg-transparent border-0 cursor-pointer p-0 mt-0.5 text-page-muted" data-testid={`collector-toggle-${c.key}`}>
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>
        <Satellite size={14} className="mt-0.5 shrink-0" style={{ color: c.status === "active" ? "#16a34a" : "var(--page-muted,#6b7280)" }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">{c.name}</span>
            <span className="text-xs text-page-muted">{c.source}</span>
          </div>
          <div className="text-xs text-page-muted">
            {c.schedule}{c.last_run_at ? ` · last run ${fmt(c.last_run_at)}${c.last_result ? ` — ${c.last_result}` : ""}` : " · never run"}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {controls.map((id) => (
              <button key={id} onClick={() => onControlClick?.(id)} title={`Show control ${id} in the register`}
                className="font-mono text-[10px] px-1.5 py-0.5 rounded border border-page-divider bg-transparent cursor-pointer text-page-muted hover:text-page-text hover:bg-page-bg">
                {id}
              </button>
            ))}
          </div>
        </div>
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap" style={{ background: st.bg, color: st.color }}>{st.label}</span>
      </div>
      {open ? (
        <div className="mt-2 ml-6 space-y-2 text-xs" data-testid={`collector-detail-${c.key}`}>
          <div className="text-page-muted">{c.description}</div>
          <div data-testid={`collector-runs-${c.key}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-page-muted mb-1">Run history</div>
            {runs === null ? <Loader2 size={12} className="animate-spin" /> :
              runs.length === 0 ? <div className="text-page-muted">No runs logged yet.</div> : (
                <div className="space-y-0.5">
                  {runs.map((r) => (
                    <div key={r.id} className="flex items-start gap-2">
                      <span className="text-page-muted shrink-0 w-32">{fmt(r.ran_at)}</span>
                      {r.ok !== false
                        ? <CircleCheck size={12} className="shrink-0 mt-0.5" style={{ color: "#16a34a" }} />
                        : <CircleX size={12} className="shrink-0 mt-0.5" style={{ color: "#dc2626" }} />}
                      <span className="min-w-0">{r.result}</span>
                    </div>
                  ))}
                </div>
              )}
          </div>
          <div className="space-y-1.5">
            {Object.entries(cfg).map(([k, v]) => (
              <label key={k} className="block space-y-0.5">
                <div className="text-[10px] text-page-muted">{k}</div>
                <input value={String(v ?? "")} onChange={(e) => setCfg((p) => ({ ...p, [k]: e.target.value }))}
                  data-testid={`collector-cfg-${c.key}-${k}`}
                  className="w-full border border-page-divider rounded px-1.5 py-1 bg-transparent text-page-text" />
              </label>
            ))}
            {Object.keys(cfg).length === 0 ? <div className="text-page-muted">No settings.</div> : null}
            <div className="text-[10px] text-page-muted">Secrets are never stored here — the credential field names what the collector runner reads from its own environment.</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => patch({ config: cfg })} disabled={busy} data-testid={`collector-save-${c.key}`}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-page-bg disabled:opacity-50">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <CircleCheck size={12} />} Save settings
            </button>
            {c.status !== "disabled" ? (
              <button onClick={() => patch({ status: "disabled" })} disabled={busy} data-testid={`collector-disable-${c.key}`}
                className="text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer text-page-muted hover:text-page-text">Disable</button>
            ) : (
              <button onClick={() => patch({ status: "needs_config" })} disabled={busy}
                className="text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer text-page-muted hover:text-page-text">Enable</button>
            )}
            {c.status === "needs_config" ? (
              <button onClick={() => patch({ status: "active" })} disabled={busy} data-testid={`collector-activate-${c.key}`}
                title="Mark active once the runner's credential is in place"
                className="text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer text-page-muted hover:text-page-text">Mark active</button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CollectorsPane({ projectId, projectSlug, onControlClick }) {
  const [collectors, setCollectors] = useState(null);
  const load = useCallback(async () => {
    if (!projectId) return;
    try { const r = await eng("collector_list", { project_id: projectId }); setCollectors(r.collectors || []); }
    catch { setCollectors([]); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  if (!projectId) return <div className="p-6 text-sm text-page-muted">This program is not tracked in the audit store yet.</div>;
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="audit-collectors-pane">
      <div>
        <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2">
          <Satellite size={20} /> Evidence collectors
        </h1>
        <div className="text-sm text-page-muted mt-1">
          Automated evidence sources for <b>{projectSlug}</b> — each collector evidences the register controls it lists.
          Active collectors run on their schedule and write <SourcePill s="compiled" /> records with source pointers; the rest need their integration configured.
        </div>
      </div>
      {collectors === null ? <Loader2 size={16} className="animate-spin" /> :
        collectors.length === 0 ? <div className="text-sm text-page-muted">No collectors registered.</div> :
        <div className="space-y-1.5">{collectors.map((c) => <CollectorCard key={c.key} c={c} projectId={projectId} onChanged={load} onControlClick={onControlClick} />)}</div>}
    </div>
  );
}

// The evidence main-pane view: register table (per-control owner / frequency /
// evidence coverage) + the evidence record list, filterable by control.
function EvidencePane({ projectId, projectSlug, control, onOpenControl, onCloseControl }) {
  const [controls, setControls] = useState(null);
  const [records, setRecords] = useState(null);
  const [autoControls, setAutoControls] = useState(new Set()); // covered by an active collector
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState("");
  const [recording, setRecording] = useState(null); // control row being recorded against
  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const [cl, el, co] = await Promise.all([
        eng("control_list", { project_id: projectId }),
        eng("evidence_list", { project_id: projectId }),
        eng("collector_list", { project_id: projectId }).catch(() => ({ collectors: [] })),
      ]);
      setControls(cl.controls || []);
      setRecords(el.evidence || []);
      const auto = new Set();
      for (const c of co.collectors || []) {
        if (c.status === "active") for (const id of jsonish(c.controls, []) || []) auto.add(id);
      }
      setAutoControls(auto);
    } catch { setControls([]); setRecords([]); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  // Download the whole package as DOCX files (Igor's evidence-package layout)
  // bundled in a zip — built fully client-side from the same engine data the
  // pane renders, so what you see is exactly what exports.
  const exportPackage = async () => {
    setExporting(true); setExportErr("");
    try {
      const full = await Promise.all((records || []).map((r) =>
        eng("evidence_get", { id: r.id, project_id: projectId }).then((res) => res.evidence || r)));
      const pkg = buildEvidencePackage({ projectName: projectSlug, controls: controls || [], records: full });
      downloadBytes(pkg.name, pkg.bytes);
    } catch (e) {
      setExportErr(String(e?.message || "Export failed."));
    } finally { setExporting(false); }
  };

  if (!projectId) {
    return <div className="p-6 text-sm text-page-muted">This project is not tracked in the audit store yet — run the first audit to register it.</div>;
  }

  // Control DETAIL page (?tab=register&control=<id>): the full control record,
  // its coverage, the record action, and every evidence record for it.
  if (control) {
    const c = (controls || []).find((x) => x.control_id === control);
    const controlRecords = (records || []).filter((r) => r.control_id === control);
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="control-detail">
        <button onClick={onCloseControl} data-testid="control-detail-back"
          className="inline-flex items-center gap-1 text-xs bg-transparent border-0 cursor-pointer text-page-muted hover:text-page-text p-0">
          <ChevronRight size={12} style={{ transform: "rotate(180deg)" }} /> Control register
        </button>
        {controls === null ? <Loader2 size={16} className="animate-spin" /> : !c ? (
          <div className="text-sm text-page-muted">Control {control} is not in this register.</div>
        ) : (
          <>
            <div>
              <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2 flex-wrap">
                <ClipboardList size={20} /> <span className="font-mono">{c.control_id}</span> {c.control}
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={c.status === "operating" ? { background: "var(--pill-green-strong-bg,#dcfce7)", color: "var(--pill-green-strong-color,#166534)" } : { background: "var(--pill-amber-bg,#fef3c7)", color: "var(--pill-amber-color,#92400e)" }}>{c.status}</span>
                {autoControls.has(c.control_id) ? <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full" title="Evidenced by an active automated collector" style={{ background: "#dbeafe", color: "#1d4ed8" }}><Zap size={9} />auto</span> : null}
                {isOverdue(c) ? <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" title={`No evidence within one ${c.frequency} period`} style={{ background: "var(--pill-amber-bg,#fef3c7)", color: "var(--pill-amber-color,#92400e)" }}>due</span> : null}
              </h1>
            </div>
            <dl className="space-y-1.5" data-testid="control-detail-fields">
              {[["TSC criteria", c.tsc], ["Owner", c.owner], ["Frequency", c.frequency], ["Applies to", c.applies_to],
                ["Required evidence", c.required_evidence], ["Evidence location", c.evidence_location],
                ["Coverage", `${c.evidence_count || 0} record(s)${c.last_evidence_at ? ` · latest ${fmt(c.last_evidence_at)}` : ""}`]]
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <div key={k} className="flex gap-2 text-sm">
                    <dt className="w-40 shrink-0 text-page-muted text-xs pt-0.5">{k}</dt>
                    <dd className="min-w-0">{String(v)}</dd>
                  </div>
                ))}
            </dl>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-sm font-semibold text-page-title">Evidence records for {c.control_id}</h2>
                <button onClick={() => setRecording(c)} data-testid="control-detail-record"
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer text-page-muted hover:text-page-text hover:bg-page-bg">
                  <Plus size={11} /> Record evidence
                </button>
              </div>
              {records === null ? <Loader2 size={16} className="animate-spin" /> :
                controlRecords.length === 0 ? <div className="text-sm text-page-muted">No evidence records for {c.control_id} yet.</div> :
                <div className="space-y-1.5">{controlRecords.map((r) => <EvidenceRow key={r.id} r={r} projectId={projectId} onChanged={load} />)}</div>}
            </div>
          </>
        )}
        {recording ? (
          <RecordEvidenceModal control={recording} projectId={projectId}
            onClose={() => setRecording(null)} onSaved={load} />
        ) : null}
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5" data-testid="audit-evidence-pane">
      <div>
        <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2">
          <ClipboardList size={20} /> Evidence register
          <button
            type="button"
            data-testid="evidence-export-btn"
            onClick={exportPackage}
            disabled={exporting || !records || !records.length}
            title="Download the register + every evidence record as DOCX documents (evidence-package layout) in one zip"
            className="ml-2 inline-flex items-center gap-1.5 text-xs font-normal px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-page-bg disabled:opacity-50"
          >
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Download evidence package
          </button>
        </h1>
        {exportErr ? <div className="text-xs text-red-600 mt-1">{exportErr}</div> : null}
        <div className="text-sm text-page-muted mt-1">
          SOC 2 control &amp; evidence operating model for <b>{projectSlug}</b> — every record carries its provenance
          (<SourcePill s="logged" /> at occurrence · <SourcePill s="compiled" /> from named system records · <SourcePill s="ceremony" /> performed &amp; recorded now); sign-off makes a record immutable.
        </div>
      </div>

      {controls === null ? <Loader2 size={16} className="animate-spin" /> : (
        <div className="overflow-x-auto border border-page-divider rounded-md">
          {/* Slim columns — the control name wraps instead of pushing ID off
              screen; TSC / applies-to / required evidence live on the
              control's DETAIL page, which every row opens. */}
          <table className="text-xs w-full border-collapse" data-testid="control-register">
            <thead>
              <tr className="text-left text-page-muted">
                {["ID", "Control", "Owner", "Frequency", "Status", "Evidence", ""].map((h, i) => <th key={i} className="px-2 py-1.5 font-medium whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {controls.map((c) => (
                <tr key={c.control_id}
                  className="border-t border-page-divider cursor-pointer hover:bg-card-border/20"
                  onClick={() => onOpenControl?.(c.control_id)}
                  title={`Open control ${c.control_id}`}
                  data-testid={`control-${c.control_id}`}>
                  <td className="px-2 py-1 font-mono text-[10px] whitespace-nowrap">{c.control_id}</td>
                  <td className="px-2 py-1 min-w-56">{c.control}</td>
                  <td className="px-2 py-1">{c.owner}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{c.frequency}</td>
                  <td className="px-2 py-1"><span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={c.status === "operating" ? { background: "var(--pill-green-strong-bg,#dcfce7)", color: "var(--pill-green-strong-color,#166534)" } : { background: "var(--pill-amber-bg,#fef3c7)", color: "var(--pill-amber-color,#92400e)" }}>{c.status}</span></td>
                  <td className="px-2 py-1 whitespace-nowrap">
                    {autoControls.has(c.control_id) ? <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full mr-1" title="Evidenced by an active automated collector" style={{ background: "#dbeafe", color: "#1d4ed8" }}><Zap size={9} />auto</span> : null}
                    {isOverdue(c) ? <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full mr-1" title={`No evidence within one ${c.frequency} period`} style={{ background: "var(--pill-amber-bg,#fef3c7)", color: "var(--pill-amber-color,#92400e)" }}>due</span> : null}
                    {c.evidence_count > 0 ? <b>{c.evidence_count}</b> : <span className="text-page-muted">0</span>}{c.last_evidence_at ? <span className="text-page-muted"> · {fmt(c.last_evidence_at)}</span> : null}
                  </td>
                  <td className="px-1 py-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); setRecording(c); }}
                      title="Record evidence for this control"
                      data-testid={`record-btn-${c.control_id}`}
                      className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border border-page-divider bg-transparent cursor-pointer text-page-muted hover:text-page-text hover:bg-page-bg"
                    >
                      <Plus size={10} /> record
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div>
        <h2 className="text-sm font-semibold text-page-title mb-2">Evidence records</h2>
        {records === null ? <Loader2 size={16} className="animate-spin" /> :
          (records || []).length === 0 ? <div className="text-sm text-page-muted">No evidence records yet.</div> :
          <div className="space-y-1.5">{records.map((r) => <EvidenceRow key={r.id} r={r} projectId={projectId} onChanged={load} />)}</div>}
      </div>

      {recording ? (
        <RecordEvidenceModal
          control={recording}
          projectId={projectId}
          onClose={() => setRecording(null)}
          onSaved={() => { onOpenControl?.(recording.control_id); load(); }}
        />
      ) : null}
    </div>
  );
}

// The one audit surface, two scopes:
//  - per-project (default): the current project's compliance program. No
//    platform section — infrastructure is cortex-core, not per-project.
//  - cortex core (program="cortex", via pages/CortexAudit.jsx): the platform's
//    own program — repo-level audits, the inherited infrastructure layer, and
//    the Mindshine control register. Runs and fixes happen through the
//    auditor engine / dev workflow, not the in-app buttons, so both are off.
export default function Audit({ program, title = "Audit", subtitle, showPlatform = false, allowRun = true, allowFix = true }) {
  const { projectSlug } = useProject();
  const programName = program || projectSlug;
  const [tracked, setTracked] = useState(null);   // soc2_project row for this slug
  const [audits, setAudits] = useState(null);
  const [issues, setIssues] = useState(null);
  const [platIssues, setPlatIssues] = useState(null); // infra-layer issues (cortex page only)
  const [platform, setPlatform] = useState(null); // { proj, audit, stats } — the inherited infra layer
  const [liveJobs, setLiveJobs] = useState([]);    // queued/claimed audit runs for this project
  // Rail/tab selection lives in the URL so every audit view is addressable,
  // shareable, and survives reload: ?tab=register|collectors|logs, plus
  // &control=<id> (register filter) and &run=<c|a|p><id> (a run's detail
  // page — the prefix encodes what it is: c = collector run, a = code audit,
  // p = platform/infra scan). No params = Overview.
  const [searchParams, setSearchParams] = useSearchParams();
  const sel = useMemo(() => {
    const run = searchParams.get("run");
    if (run) return { kind: "rundetail", runId: run };
    const tab = searchParams.get("tab");
    if (tab === "register") return { kind: "evidence", control: searchParams.get("control") || null };
    if (tab === "collectors") return { kind: "collectors" };
    if (tab === "logs") return { kind: "logs" };
    return null;
  }, [searchParams]);
  const setSel = useCallback((next) => {
    const p = {};
    if (next?.kind === "evidence") { p.tab = "register"; if (next.control) p.control = next.control; }
    else if (next?.kind === "collectors") p.tab = "collectors";
    else if (next?.kind === "logs") p.tab = "logs";
    else if (next?.kind === "rundetail") { p.tab = "logs"; p.run = String(next.entry?.id ?? next.runId ?? ""); }
    setSearchParams(p);
  }, [setSearchParams]);
  const [layerFilter, setLayerFilter] = useState(null); // null | 'code' | 'infra'
  const [running, setRunning] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [railOpen, setRailOpen] = useState(() => {
    try { return localStorage.getItem("auditRailOpen") !== "0"; } catch { return true; }
  });
  const toggleRail = () => setRailOpen((o) => { const n = !o; try { localStorage.setItem("auditRailOpen", n ? "1" : "0"); } catch {} return n; });

  const refresh = useCallback(async () => {
    setErr("");
    try {
      try {
        const lj = await fetchData("audit/jobs", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ target: programName }) });
        setLiveJobs(lj?.jobs || []);
      } catch { setLiveJobs([]); }
      const pl = await eng("project_list", {});
      const rows = pl.projects || [];
      const mine = rows.find((p) => p.name === programName) || null;
      setTracked(mine);
      if (mine) {
        const [al, il] = await Promise.all([
          eng("audit_list", { project_id: mine.id }),
          eng("issue_list", { project_id: mine.id }),
        ]);
        setAudits(al.audits || []);
        setIssues(il.issues || []);
      } else { setAudits([]); setIssues([]); }
      if (showPlatform) {
        const plat = rows.find((p) => p.name === "platform");
        if (plat) {
          const [pa, pi] = await Promise.all([
            eng("audit_list", { project_id: plat.id }),
            eng("issue_list", { project_id: plat.id }).catch(() => ({ issues: [] })),
          ]);
          const latest = (pa.audits || [])[0];
          // Raw scan stats are DETECTIONS (the scanner re-detects accepted
          // risks every run, and passing checks are not ingested at all) —
          // the governed state is the issue ledger's open count.
          setPlatform(latest ? { proj: plat, audit: latest, stats: jsonish(latest.stats, {}), open: plat.open_issues | 0 } : null);
          setPlatIssues(pi.issues || []);
        }
      }
    } catch (e) {
      setErr(String(e?.message || "The audit service is not reachable."));
      setAudits((p) => p ?? []); setIssues((p) => p ?? []);
    }
  }, [programName, showPlatform]);
  // The URL is the selection's source of truth — deep links must survive the
  // initial load, so refresh() never resets it.
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!liveJobs.length) return;
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, [liveJobs.length, refresh]);

  const runAudit = async () => {
    setRunning(true); setMsg(""); setSel(null);
    try {
      await fetchData("audit/run", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ target: programName }) });
      setMsg("Audit queued — the auditor agent picks it up within seconds; results appear here.");
      setTimeout(refresh, 15000); setTimeout(refresh, 45000);
    } catch (e) { setMsg(String(e?.message || "Failed to queue the audit.")); }
    finally { setRunning(false); }
  };

  // Queue open issues to the PROJECT ASSISTANT as a change request (the same
  // closed-loop path the auditor uses): one issue or all of them.
  const fileFix = async (list) => {
    const items = (list || []).filter((i) => ["open", "in_progress"].includes(i.status));
    if (!items.length) return;
    setFixing(true); setMsg("");
    try {
      const lines = [`Fix the following ${items.length} compliance finding(s) from the project audit (see the Audit page):`];
      for (const i of items) lines.push("", `- **${i.control_id} ${i.title}** (${i.severity})`, `  ${i.detail || ""}`.trimEnd());
      lines.push("", "Fix ALL of them; do not touch anything unrelated. Re-audit happens automatically on completion.", "[audit-round:1]");
      const r = await fetchData("app_requests", { method: "POST", headers: { "Content-Type": "application/json" },
        // change_project: project-level fixes have no app target — plain
        // 'change' requires an app_id and 400s.
        body: JSON.stringify({ kind: "change_project", title: `Audit fixes: ${items.length} finding(s)`, details: lines.join("\n") }) });
      const id = (r?.request || r)?.id;
      setMsg(`Queued to the project assistant (request #${id}) — track it on the Board; the audit re-runs automatically when it completes.`);
    } catch (e) { setMsg(String(e?.message || "Failed to queue the fix request.")); }
    finally { setFixing(false); }
  };

  const latest = (audits || [])[0];
  const latestStats = latest ? jsonish(latest.stats, {}) || {} : null;
  const score = useMemo(() => {
    if (!latestStats) return null;
    const p = latestStats.pass || 0, f = latestStats.fail || 0;
    return p + f ? Math.round((p / (p + f)) * 100) : null;
  }, [latestStats]);

  // ONE issue list: the program's own (code) issues merged with the platform
  // (infra) layer's, each row tagged with its layer — re-ranked so open +
  // severe issues lead regardless of which store they came from.
  const allIssues = useMemo(() => {
    const STATUS_RANK = { open: 0, in_progress: 1 };
    const SEV_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
    const merged = [
      ...(issues || []).map((i) => ({ ...i, _layer: "code" })),
      ...(platIssues || []).map((i) => ({ ...i, _layer: "infra" })),
    ];
    merged.sort((a, b) =>
      (STATUS_RANK[a.status] ?? 2) - (STATUS_RANK[b.status] ?? 2) ||
      (SEV_RANK[a.severity] ?? 4) - (SEV_RANK[b.severity] ?? 4) ||
      a.id - b.id);
    return merged;
  }, [issues, platIssues]);
  const openCount = allIssues.filter((i) => ["open", "in_progress"].includes(i.status)).length;

  const Section = ({ label, children }) => (
    <div className="mb-2">
      <div className="px-2 pt-2 pb-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-page-muted">{label}</span>
      </div>
      {children}
    </div>
  );

  return (
    <div className="flex h-full min-h-0" data-testid="audit-page">
      {!railOpen && (
        <div className="w-10 shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col items-center py-3 gap-2">
          <button
            type="button"
            data-testid="audit-rail-open"
            onClick={toggleRail}
            title="Show audit runs"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <PanelLeftOpen size={18} />
          </button>
          <ShieldCheck size={16} className="text-nav-active mt-1" />
        </div>
      )}

      {/* Left rail: audit runs explorer (collapsible), mirroring Apps/Agents */}
      <aside className={(railOpen ? "w-72" : "hidden") + " shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col min-h-0"}>
        <div className="px-4 py-4 flex items-center gap-2 border-b border-card-border">
          <ShieldCheck size={18} className="text-nav-active" />
          <span className="font-semibold text-page-text">{title}</span>
          <button
            type="button"
            data-testid="audit-refresh"
            onClick={refresh}
            title="Refresh"
            className="ml-auto text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <RefreshCw size={14} />
          </button>
          <button
            type="button"
            data-testid="audit-rail-close"
            onClick={toggleRail}
            title="Collapse audit list"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <PanelLeftClose size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {/* Overview entry — the default main-pane view (score + issues). */}
          <button
            type="button"
            data-testid="audit-overview-row"
            onClick={() => setSel(null)}
            className={
              "w-full text-left px-3 py-2 flex items-center gap-2 cursor-pointer border-0 rounded-md text-sm " +
              (sel == null ? "bg-nav-active/10 text-nav-active" : "bg-transparent text-page-text hover:bg-card-border/40")
            }
          >
            <Gauge size={15} className="shrink-0" />
            <span className="min-w-0 flex flex-col">
              <span className="font-medium">Overview</span>
              <span className="text-xs text-page-muted">
                {score == null && !allIssues.length ? "no audits yet" : `${score == null ? "" : `${score}% · `}${openCount} open issue(s)`}
              </span>
            </span>
          </button>

          {liveJobs.length > 0 && (
            <div className="flex items-center gap-2 text-xs rounded-md px-3 py-2 mt-1" data-testid="audit-live-jobs"
              style={{ background: "var(--severity-warning-bg,#fef3c7)", color: "var(--severity-warning-text,#92400e)" }}>
              <Loader2 size={13} className="animate-spin shrink-0" />
              {liveJobs.length === 1
                ? `Audit ${liveJobs[0].status === "claimed" ? "running" : "queued"} (${liveJobs[0].trigger || "manual"})`
                : `${liveJobs.length} audits in flight`}
            </div>
          )}

          {tracked && (
            <Section label="Evidence">
              <button
                type="button"
                data-testid="audit-evidence-row"
                onClick={() => setSel({ kind: "evidence" })}
                className={
                  "w-full text-left px-3 py-2 flex items-center gap-2 cursor-pointer border-0 rounded-md text-sm " +
                  (sel?.kind === "evidence" ? "bg-nav-active/10 text-nav-active" : "bg-transparent text-page-text hover:bg-card-border/40")
                }
              >
                <ClipboardList size={15} className="shrink-0" />
                <span className="min-w-0 flex flex-col">
                  <span className="font-medium">Control register</span>
                  <span className="text-xs text-page-muted">controls · evidence records · sign-off</span>
                </span>
              </button>
              <button
                type="button"
                data-testid="audit-collectors-row"
                onClick={() => setSel({ kind: "collectors" })}
                className={
                  "w-full text-left px-3 py-2 flex items-center gap-2 cursor-pointer border-0 rounded-md text-sm " +
                  (sel?.kind === "collectors" ? "bg-nav-active/10 text-nav-active" : "bg-transparent text-page-text hover:bg-card-border/40")
                }
              >
                <Satellite size={15} className="shrink-0" />
                <span className="min-w-0 flex flex-col">
                  <span className="font-medium">Collectors</span>
                  <span className="text-xs text-page-muted">automated evidence sources · configuration</span>
                </span>
              </button>
              <button
                type="button"
                data-testid="audit-logs-row"
                onClick={() => setSel({ kind: "logs" })}
                className={
                  "w-full text-left px-3 py-2 flex items-center gap-2 cursor-pointer border-0 rounded-md text-sm " +
                  (sel?.kind === "logs" || sel?.kind === "rundetail" ? "bg-nav-active/10 text-nav-active" : "bg-transparent text-page-text hover:bg-card-border/40")
                }
              >
                <ClipboardList size={15} className="shrink-0" />
                <span className="min-w-0 flex flex-col">
                  <span className="font-medium">Logs</span>
                  <span className="text-xs text-page-muted">collector &amp; scanner run history</span>
                </span>
              </button>
            </Section>
          )}
        </div>

        {/* Rail footer: the primary action, mirroring "Build an agent".
            Hidden on the cortex-core page — repo/platform audits run from the
            auditor engine, not the in-app queue. */}
        {allowRun && (
          <div className="p-2 border-t border-card-border">
            <button
              type="button"
              data-testid="audit-run-btn"
              onClick={runAudit}
              disabled={running}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-dashed border-card-border bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30 cursor-pointer disabled:opacity-50"
            >
              {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Run audit
            </button>
          </div>
        )}
      </aside>

      {/* Main pane: overview (score + the unified tagged issue list) by default */}
      <main className="flex-1 min-w-0 min-h-0 overflow-y-auto bg-page-bg">
        {sel?.kind === "logs" ? (
          <LogsPane programId={tracked?.id} platformId={showPlatform ? platform?.proj?.id : null} programSlug={programName}
            onOpen={(entry) => setSel({ kind: "rundetail", entry })} />
        ) : sel?.kind === "rundetail" ? (
          (() => {
            // Reconstruct the run reference from the URL param: c<id> = a
            // collector run, a<id> = a code audit, p<id> = a platform scan.
            const rid = String(sel.runId || "");
            const type = rid[0] === "c" ? "collector" : "scan";
            const layer = rid[0] === "p" ? "infra" : "code";
            const id = parseInt(rid.slice(1), 10);
            const projectId = rid[0] === "p" ? platform?.proj?.id : tracked?.id;
            if (!id || !projectId) return <div className="p-6"><Loader2 size={16} className="animate-spin" /></div>;
            return <RunDetailPane key={rid} runRef={{ type, id, layer, projectId }} platformId={platform?.proj?.id}
              onBack={() => setSel({ kind: "logs" })}
              onJumpControl={(cid) => setSel({ kind: "evidence", control: cid })}
              onOpenRun={(rid) => setSel({ kind: "rundetail", runId: rid })} />;
          })()
        ) : sel?.kind === "evidence" ? (
          <EvidencePane projectId={tracked?.id} projectSlug={programName} control={sel.control || null}
            onOpenControl={(cid) => setSel({ kind: "evidence", control: cid })}
            onCloseControl={() => setSel({ kind: "evidence" })} />
        ) : sel?.kind === "collectors" ? (
          <CollectorsPane projectId={tracked?.id} projectSlug={programName}
            onControlClick={(id) => setSel({ kind: "evidence", control: id })} />
        ) : (
          <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="audit-overview">
            <div>
              <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2">
                <ShieldCheck size={20} /> {title}
              </h1>
              <div className="text-sm text-page-muted mt-1">{subtitle || <>standards compliance for <b>{programName}</b></>}</div>
            </div>
            {msg ? <div className="text-xs text-page-muted">{msg}</div> : null}
            {err ? <div className="text-sm text-red-600">{err}</div> : null}

            {/* summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border border-page-divider p-3">
                <div className="text-xs text-page-muted">Compliance score</div>
                <div className="text-2xl font-semibold" style={{ color: score == null ? "var(--page-muted)" : score >= 80 ? "#16a34a" : score >= 50 ? "#d97706" : "#dc2626" }}>
                  {score == null ? "—" : `${score}%`}
                </div>
              </div>
              <div className="rounded-lg border border-page-divider p-3">
                <div className="text-xs text-page-muted">Open issues</div>
                <div className="text-2xl font-semibold">{issues == null ? "—" : openCount}</div>
              </div>
              <div className="rounded-lg border border-page-divider p-3">
                <div className="text-xs text-page-muted">Audit runs</div>
                <div className="text-2xl font-semibold">{audits == null ? "—" : audits.length}</div>
              </div>
              {showPlatform && (
                <div className="rounded-lg border border-page-divider p-3">
                  <div className="text-xs text-page-muted inline-flex items-center gap-1"><Server size={11} /> Platform layer (infrastructure)</div>
                  <div className="text-sm mt-1">
                    {platform ? <>
                      <span style={{ color: platform.open > 0 ? "#b91c1c" : "#166534" }}>{platform.open} open</span>
                      <span className="text-page-muted text-xs"> · {(platform.stats.fail || 0) + (platform.stats.manual || 0)} detections incl. accepted risks ({fmt(platform.audit.started_at || platform.audit.created_at)})</span>
                    </> : <span className="text-page-muted text-xs">no infra scan yet</span>}
                  </div>
                </div>
              )}
            </div>

            {/* the unified issue ledger: code + infra together, every row
                tagged with its layer and its detector, expandable to detail */}
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h2 className="text-sm font-semibold text-page-title">Open issues</h2>
                {(platIssues || []).length > 0 ? (
                  <span className="inline-flex items-center gap-1" data-testid="issue-layer-filter">
                    {[null, "code", "infra"].map((f) => (
                      <button key={f || "all"} onClick={() => setLayerFilter(f)}
                        className={"text-[11px] px-2 py-0.5 rounded-full border cursor-pointer " +
                          (layerFilter === f ? "border-nav-active text-nav-active bg-nav-active/10" : "border-page-divider text-page-muted bg-transparent hover:text-page-text")}>
                        {f || "all"}
                      </button>
                    ))}
                  </span>
                ) : null}
                <button onClick={() => setShowResolved((v) => !v)} data-testid="audit-show-resolved"
                  className="text-xs bg-transparent border-0 cursor-pointer hover:underline text-page-muted">
                  {showResolved ? "hide resolved history" : `show resolved history (${allIssues.filter((i) => !["open", "in_progress"].includes(i.status)).length})`}
                </button>
                {allowFix && (issues || []).some((i) => ["open", "in_progress"].includes(i.status)) ? (
                  <button onClick={() => fileFix(issues)} disabled={fixing} data-testid="audit-fix-all"
                    className="ml-auto inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-page-bg disabled:opacity-50">
                    {fixing ? <Loader2 size={13} className="animate-spin" /> : <Wrench size={13} />}
                    Fix all open with the assistant
                  </button>
                ) : null}
              </div>
              {issues == null ? <Loader2 size={16} className="animate-spin" /> : (() => {
                const pool = allIssues.filter((i) => !layerFilter || i._layer === layerFilter);
                const shown = showResolved ? pool : pool.filter((i) => ["open", "in_progress"].includes(i.status));
                if (!shown.length) return <div className="text-sm text-page-muted">No open issues{latest ? " — all checks pass or are attested." : " — run the first audit."}</div>;
                // "Fix with assistant" queues to the PROJECT assistant — only
                // meaningful for code-layer issues; infra remediation is
                // console/IaC work dispositioned through the ledger.
                return <div className="space-y-1.5">{shown.map((i) => <IssueRow key={`${i._layer}-${i.id}`} i={i} onFix={allowFix && i._layer === "code" ? (x) => fileFix([x]) : null} busy={fixing} />)}</div>;
              })()}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
